import React, { useState, useEffect } from "react";
import EmailFilterBar, { EMAIL_FILTER_BAR_FIELDS } from "./EmailFilterBar";
import EmailOverviewView from "./EmailOverviewView";
import EmailPerformanceView from "./EmailPerformanceView";
import EmailBehavioralActionsView from "./EmailBehavioralActionsView";
import EmailDeliverabilityView from "./EmailDeliverabilityView";
import EmailReputationView from "./EmailReputationView";
import EmailAudienceView from "./EmailAudienceView";
import EmailCreativeView from "./EmailCreativeView";
import EmailHygieneReconciliationView from "./EmailHygieneReconciliationView";
import {
  getEmailMarketingProgramHierarchy,
  getEmailMarketingAngles,
  getBroadcasts,
  getEmailMarketingAudienceDefinitions,
} from "../../lib/emailMarketingApi";
import { useEmailFilterState } from "../../hooks/useEmailFilterState";

/**
 * EmailDashboardPanel — War Room "Email" tab content.
 *
 * Checkpoint 1: KPI shell only, backed by a mock adapter.
 * Checkpoint 2: adds the contextual header (identity block, View selector,
 * Date Range, Environment, Filters) and the six-view framework.
 * Checkpoint 3.1: Overview (EmailOverviewView) becomes an executive
 * acquisition dashboard.
 * Checkpoint 3.2: Performance (EmailPerformanceView) adds a
 * Program/Campaign/Broadcast-scoped acquisition breakdown.
 * Checkpoint 3.4 - 3.4D: control-bar consolidation, responsive one-row
 * layout, and a series of Emergent-preview-verified visual corrections
 * (see EmailFilterBar.jsx / EmailViewSelector.jsx for that history).
 *
 * Checkpoint 3.4E: the entire query-filter surface (Period, Environment,
 * Broadcast, Program, Campaign, and Custom Period ranges) moved into
 * EmailFilterBar.jsx, backed by useEmailFilterState — a single shared
 * draft/applied state owner for BOTH Overview and Performance, so:
 *
 * - the two views share one filtering vocabulary and interaction instead
 *   of each growing its own (Overview previously had none at all beyond
 *   Period/Environment — it is now Program/Campaign/Broadcast-filterable
 *   too, see emailDashboardMockAdapter.js);
 * - a filter selection made on one view remains the active dataset when
 *   switching to the other, because both views are always fed the SAME
 *   `filterState.applied` object, never two independent copies;
 * - dropdown/date changes update draft state only — the expensive data
 *   query only fires when Apply atomically promotes the whole draft to
 *   applied (see useEmailFilterState.js).
 *
 * `view` itself stays owned here, separately from filterState, exactly as
 * before — it is immediate navigation, not a query filter, and is not
 * Apply-controlled.
 *
 * Checkpoint 3.5A: adds Deliverability & Hygiene (EmailDeliverabilityView)
 * as the first real view beyond Overview/Performance, still sharing the
 * one filterState instance (Environment is a legitimately cross-view
 * concept), but with a NARROWED EmailFilterBar field configuration —
 * SendGridRecipientState (the authoritative recipient-state model) has no
 * Period/Broadcast/Program/Campaign concept, so this is the one place in
 * the codebase that inspects `view` to choose which EMAIL_FILTER_BAR_FIELDS
 * config to pass, rather than EmailFilterBar itself branching on `view`.
 * `audience` and the remaining views are untouched — still the generic
 * placeholder, still the "full" implicit field default.
 *
 * Checkpoint 3.5D: adds Reputation (EmailReputationView), reusing the same
 * environmentOnly field configuration as Deliverability — no per-broadcast
 * or per-program reputation concept exists in the backend (see
 * emailReputationMockAdapter.js's header). `audience` and `creative`
 * remain untouched placeholders.
 *
 * DASH-EMAIL-004: adds Audience & Acquisition (EmailAudienceView).
 * DASH-EMAIL-P1-IMPL-008/008A retired its fabricated content and its own
 * field configuration down to EMAIL_FILTER_BAR_FIELDS.unavailable (no
 * operative dimension at all — Audience has no authoritative operational
 * data of any kind; see EmailFilterBar.jsx).
 *
 * DASH-EMAIL-005b: added Creative (EmailCreativeView) as a flat,
 * BROADCAST_ROWS_BASE-re-projecting surface. DASH-EMAIL-011a retired that
 * model entirely and rebuilt Creative Analytics against the governed
 * Program/Campaign/Angle/Creative/Version/Asset hierarchy, using
 * EMAIL_FILTER_BAR_FIELDS.creativeAnalytics (Period/Environment/Program/
 * Campaign, no Broadcast — see that config's own comment) plus Angle,
 * Creative Analytics' one contextual dimension.
 *
 * DASH-EMAIL-006a: every EMAIL_VIEWS id now has an explicit routing
 * branch below — the generic placeholder view that used to exist as a
 * defensive fallback was removed (it was provably unreachable: `view` is
 * driven entirely by EmailViewSelector, itself driven by EMAIL_VIEWS, so
 * it can never hold a value outside the branches below).
 *
 * DASH-EMAIL-011b added Performance <-> Creative Analytics cross-navigation
 * (a Broadcast row's "View Creative" action, and a Creative Version's
 * Broadcast-usage row's "View in Performance" action), backed by an
 * explicit mock<->mock reconciliation map since both surfaces were mock at
 * the time. DASH-EMAIL-012A removed this cross-navigation entirely (a
 * deliberate, temporary regression, not an oversight): Creative Analytics
 * now uses the real governed Broadcast catalog ("BRC####" ids), while
 * Performance still uses its own independent synthetic catalog ("brd-*"
 * ids, emailPerformanceMockAdapter.js) — reusing the old reconciliation map
 * against one real and one mock catalog would fabricate a cross-system
 * identity claim neither side can actually support. Restoring this is
 * explicit follow-up work for whenever Performance itself is cut over to
 * the real catalog, not part of this checkpoint. EMAIL_FILTER_BAR_FIELDS is
 * still imported/used for `filterFields` below — only the cross-navigation
 * apply-effect (which also lived in this component) was removed.
 */
export default function EmailDashboardPanel() {
  const [view, setView] = useState("overview");
  const filterState = useEmailFilterState();
  const { applied } = filterState;

  // DASH-EMAIL-013: the Email Hygiene Reconciliation queue/detail surface
  // is reached ONLY by drilling into Deliverability & Hygiene's existing
  // "Reconciliation Required" tile — deliberately NOT a seventh
  // EMAIL_VIEWS tab (no "SendGrid -> Reconciliation" top-level nav item;
  // see EmailHygieneReconciliationView.jsx's own header comment). This is
  // local drill-in state, independent of `view`/`filterState` entirely —
  // opening it replaces the normal EmailFilterBar + view content below
  // with the reconciliation surface until the operator clicks "Back to
  // Deliverability & Hygiene", exactly mirroring EmailCreativeView's own
  // selectedCreativeId drill-down pattern one level up.
  const [reconciliationOpen, setReconciliationOpen] = useState(false);

  // DASH-EMAIL-012A: the shared Program/Campaign filter dropdown
  // (EmailFilterBar's `programs` prop) had always been sourced from
  // Performance's own synthetic PROGRAM_HIERARCHY ("prog-*"/"camp-*" ids)
  // — completely unrelated to the real governed catalog's ids ("PRG001"/
  // "CMP0001"). Wiring Creative Analytics' Program/Campaign filter to real
  // query params (getCreativeAnalyticsCreatives({program_id, campaign_id}))
  // while the dropdown itself still offered PROGRAM_HIERARCHY's ids would
  // silently match zero rows for any non-"all" selection — a material,
  // previously-undiscovered contract gap found during 012A implementation
  // (see the completion report). Creative Analytics therefore got its OWN
  // real Program/Campaign hierarchy fetch here.
  //
  // DASH-EMAIL-P1-IMPL-032: renamed from `creativeProgramHierarchy` --
  // this SAME real hierarchy is now also passed to Overview's and
  // Performance's own governed Program/Campaign selectors (see
  // EmailFilterBar.jsx's own `governedProgramOptions`/
  // `governedCampaignOptions` -- they consume this exact prop shape, just
  // independently/flattened rather than cascaded). Deliverability/
  // Reputation/Audience remain untouched -- they never render a Program/
  // Campaign control of any kind.
  const [realProgramHierarchy, setRealProgramHierarchy] = useState([]);
  // Real Angle options for EmailFiltersControl's Angle <Select> (Creative
  // Analytics' one contextual filter) — same real-vs-mock-id problem as
  // Program/Campaign above did not apply to Angle (it was already sourced
  // from the Creative Analytics adapter specifically, never from
  // PROGRAM_HIERARCHY), but a *static* import can no longer supply
  // governed, tenant-scoped Angle identities now that the adapter is gone
  // — fetched here alongside the hierarchy since both are the same kind of
  // one-time reference data.
  const [creativeAngleOptions, setCreativeAngleOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [hierarchy, angles] = await Promise.all([getEmailMarketingProgramHierarchy(), getEmailMarketingAngles()]);
        if (cancelled) return;
        setRealProgramHierarchy(hierarchy);
        setCreativeAngleOptions(angles.map((a) => ({ value: a.angle_id, label: a.name })));
      } catch {
        // Non-critical reference data — empty lists just mean these
        // dropdowns offer only "All" until this loads.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // DASH-EMAIL-P1-IMPL-006: the real, governed Broadcast catalog
  // (`{broadcast_id, name}[]`) for Overview's top-bar Broadcast selector —
  // structurally distinct from PROGRAM_HIERARCHY's mock "brd-*" ids above,
  // same one-time-reference-data-fetch pattern as realProgramHierarchy.
  // DASH-EMAIL-P1-IMPL-032: renamed from `overviewBroadcastOptions` --
  // now also consumed by Performance's identical governed Broadcast
  // selector.
  const [governedBroadcastOptions, setGovernedBroadcastOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { items } = await getBroadcasts({ limit: 100 });
        if (cancelled) return;
        setGovernedBroadcastOptions(items || []);
      } catch {
        // Non-critical reference data — empty list just means the
        // dropdown offers only "All Broadcasts" until this loads.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // DASH-EMAIL-P1-IMPL-032: the real, governed Audience-Definition catalog
  // (`{audience_id, name, status}[]`) for Overview's/Performance's governed
  // Audience selector -- same one-time-reference-data-fetch pattern as
  // governedBroadcastOptions above.
  const [governedAudienceOptions, setGovernedAudienceOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await getEmailMarketingAudienceDefinitions();
        if (cancelled) return;
        setGovernedAudienceOptions(items || []);
      } catch {
        // Non-critical reference data — empty list just means the
        // dropdown offers only "All Audiences" until this loads.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // DASH-EMAIL-P1-IMPL-008A: Audience gets `unavailable` (renamed from
  // `periodEnvironment`) -- it has no authoritative operational data of
  // any kind, so Period/Environment are no longer treated as real filters
  // for it either; see EmailFilterBar.jsx's own comment on that config.
  //
  // DASH-EMAIL-P1-IMPL-032: Performance moves from `periodOnly` to its own
  // `performance` config (#350/#351 removed the EDT/LPE filtering
  // asymmetry `periodOnly` existed to work around -- see
  // EmailFilterBar.jsx's own comment on both configs).
  const filterFields =
    view === "deliverability" || view === "reputation"
      ? EMAIL_FILTER_BAR_FIELDS.environmentOnly
      : view === "audience"
        ? EMAIL_FILTER_BAR_FIELDS.unavailable
        : view === "creative"
          ? EMAIL_FILTER_BAR_FIELDS.creativeAnalytics
          : view === "overview"
            ? EMAIL_FILTER_BAR_FIELDS.overview
            : view === "performance"
              ? EMAIL_FILTER_BAR_FIELDS.performance
              : view === "behavioral"
                ? EMAIL_FILTER_BAR_FIELDS.behavioral
                : EMAIL_FILTER_BAR_FIELDS.full;

  if (reconciliationOpen) {
    return (
      <div className="space-y-6" data-testid="email-dashboard-panel">
        <EmailHygieneReconciliationView onBack={() => setReconciliationOpen(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-dashboard-panel">
      <EmailFilterBar
        view={view}
        onViewChange={setView}
        // DASH-EMAIL-P1-IMPL-032: Overview/Performance now also receive
        // the REAL, governed hierarchy -- their own
        // `fields.governedProgramId`/`governedCampaignId` render the
        // governed selectors that consume it (`fields.program`/`campaign`
        // are false for both, so the mock-cascaded cells never render for
        // either view regardless). Every real-data view (Overview,
        // Performance, Creative Analytics) now shares this single fetch;
        // Deliverability/Reputation/Audience never render a Program/
        // Campaign control at all, so the value passed for them is inert.
        programs={realProgramHierarchy}
        angleOptions={creativeAngleOptions}
        broadcastOptions={governedBroadcastOptions}
        audienceOptions={governedAudienceOptions}
        filterState={filterState}
        fields={filterFields}
      />

      {/* View content — every view receives the same applied filter set */}
      {view === "overview" ? (
        <EmailOverviewView
          period={applied.period}
          environment={applied.environment}
          customRange={applied.customRange}
          governedBroadcastId={applied.governedBroadcastId}
          governedAudienceId={applied.governedAudienceId}
          governedProgramId={applied.governedProgramId}
          governedCampaignId={applied.governedCampaignId}
          governedState={applied.governedState}
        />
      ) : view === "performance" ? (
        <EmailPerformanceView
          period={applied.period}
          customRange={applied.customRange}
          environment={applied.environment}
          governedBroadcastId={applied.governedBroadcastId}
          governedAudienceId={applied.governedAudienceId}
          governedProgramId={applied.governedProgramId}
          governedCampaignId={applied.governedCampaignId}
          governedState={applied.governedState}
        />
      ) : view === "behavioral" ? (
        <EmailBehavioralActionsView
          period={applied.period}
          customRange={applied.customRange}
          environment={applied.environment}
          governedBroadcastId={applied.governedBroadcastId}
          governedAudienceId={applied.governedAudienceId}
          governedProgramId={applied.governedProgramId}
          governedCampaignId={applied.governedCampaignId}
          governedState={applied.governedState}
        />
      ) : view === "deliverability" ? (
        <EmailDeliverabilityView
          environment={applied.environment}
          onOpenReconciliation={() => setReconciliationOpen(true)}
        />
      ) : view === "reputation" ? (
        <EmailReputationView environment={applied.environment} />
      ) : view === "audience" ? (
        <EmailAudienceView />
      ) : view === "creative" ? (
        <EmailCreativeView
          period={applied.period}
          program={applied.program}
          campaign={applied.campaign}
          angle={applied.angle}
        />
      ) : null}
    </div>
  );
}
